/**
 * 通用脚本加载器
 * 用于管理第三方库的加载，避免重复加载，支持依赖管理
 *
 * 使用示例（URL 已预定义，超简洁）：
 *
 * // 方式 1：加载单个库
 * ScriptLoader.loadLibrary('embla', () => {
 *   console.log('Embla loaded!');
 * });
 *
 * // 方式 2：批量加载（数组形式，最简洁）
 * ScriptLoader.loadLibraries(['embla', 'emblaAutoplay'], () => {
 *   console.log('All loaded!');
 *   const embla = EmblaCarousel(node, options, [EmblaCarouselAutoplay()]);
 * });
 *
 * // 方式 3：自定义配置覆盖
 * ScriptLoader.loadLibrary('jquery', () => {
 *   console.log('jQuery ready');
 * }, { async: true }); // 覆盖默认的 defer
 *
 * // 方式 4：批量加载 + 部分自定义
 * ScriptLoader.loadLibraries([
 *   'embla',
 *   { name: 'jquery', options: { async: true } }
 * ], callback);
 *
 * // 方式 5：手动指定所有配置（未预定义的库）
 * ScriptLoader.load('customLib', '/path/to/lib.js', {
 *   globalCheck: 'CustomLib',
 *   defer: true
 * }, () => {
 *   console.log('Custom lib loaded');
 * });
 *
 * // 可用的预定义库：embla, emblaAutoplay, jquery, swiper, gsap, scrollTrigger
 */

// 预定义库配置映射（类似 TypeScript 的类型定义）
const LIBRARY_CONFIG = {
  'embla': {
    url: '//satellai.com/cdn/shop/t/164/assets/embla-carousel.umd@8.6.0.js?v=154771677672264027361775035900',
    globalCheck: 'EmblaCarousel',
    defer: false,
    async: false
  },
  'emblaAutoplay': {
    url: '//satellai.com/cdn/shop/t/164/assets/embla-carousel-autoplay@8.6.0.js?v=67556939967295393731775035899',
    globalCheck: 'EmblaCarouselAutoplay',
    defer: true,
    async: false
  },
  'gsap': {
    url: '//satellai.com/cdn/shop/t/164/assets/gsap@3.13.0.js?v=152662349883759272671775035897',
    globalCheck: 'gsap',
    defer: true,
    async: false
  },
  'scrollTrigger': {
    url: '//satellai.com/cdn/shop/t/164/assets/ScrollTrigger.min.js?v=135539571191126401401775035897',
    globalCheck: 'ScrollTrigger',
    defer: true,
    async: false
  },
  'jquery': {
    url: '//satellai.com/cdn/shop/t/164/assets/jquery.min.js?v=147293088974801289311775035899',
    globalCheck: 'jQuery',
    defer: true,
    async: false
  },
  'swiper': {
    url: '//satellai.com/cdn/shop/t/164/assets/swiper-bundle.min.js?v=78603704230365145241775035900',
    globalCheck: 'Swiper',
    defer: true,
    async: false
  },
  "tippy": {
    url:  '//satellai.com/cdn/shop/t/164/assets/tippy.js@6.js?v=181904009406608139751775035899',
    globalCheck: 'tippy',
    defer: true,
  },
  "popper": {
    url: '//satellai.com/cdn/shop/t/164/assets/@popperjs.js?v=134854811196925441181775035897',
    globalCheck: 'Popper',
    defer: true,
  }
};

class ScriptLoaderClass {
  constructor() {
    // 已加载的脚本记录 { key: { status: 'loading'|'loaded'|'error', callbacks: [] } }
    this.scripts = {};
  }

  /**
   * 检查脚本是否已加载
   * @param {string} key - 脚本唯一标识
   * @param {string} globalCheck - 全局对象检测（可选）
   * @returns {boolean}
   */
  isLoaded(key, globalCheck) {
    // 检查是否在记录中
    if (this.scripts[key] && this.scripts[key].status === 'loaded') {
      return true;
    }

    // 检查全局对象
    if (globalCheck && typeof window[globalCheck] !== 'undefined') {
      // 更新记录
      if (!this.scripts[key]) {
        this.scripts[key] = { status: 'loaded', callbacks: [] };
      } else {
        this.scripts[key].status = 'loaded';
      }
      return true;
    }

    return false;
  }

  /**
   * 加载单个脚本
   * @param {string} key - 脚本唯一标识
   * @param {string} url - 脚本 URL
   * @param {Object} options - 配置选项
   * @param {boolean} options.defer - 是否 defer（默认 true）
   * @param {boolean} options.async - 是否 async（默认 false）
   * @param {string} options.globalCheck - 全局对象检测
   * @param {boolean} options.useIdleCallback - 是否使用 requestIdleCallback（默认 true）
   * @param {Function} options.onProgress - 加载进度回调
   * @param {Function} options.onError - 错误回调
   * @param {Function} callback - 加载完成回调
   */
  load(key, url, options = {}, callback) {
    // 参数处理：支持 load(key, url, callback) 简写
    if (typeof options === 'function') {
      callback = options;
      options = {};
    }

    // 默认配置
    const config = {
      defer: true,
      async: false,
      useIdleCallback: true,
      ...options
    };

    // 检查是否已加载
    if (this.isLoaded(key, config.globalCheck)) {
      if (callback) {
        // 使用 requestIdleCallback 优化性能
        if (config.useIdleCallback && window.requestIdleCallback) {
          requestIdleCallback(() => callback());
        } else {
          callback();
        }
      }
      return;
    }

    // 检查是否正在加载
    if (this.scripts[key]) {
      if (this.scripts[key].status === 'loading') {
        // 添加到回调队列
        if (callback) {
          this.scripts[key].callbacks.push(callback);
        }
        return;
      }
      if (this.scripts[key].status === 'error') {
        console.warn(`Script "${key}" previously failed to load. Retrying...`);
      }
    }

    // 初始化记录
    this.scripts[key] = {
      status: 'loading',
      callbacks: callback ? [callback] : []
    };

    // 创建 script 标签
    const script = document.createElement('script');
    script.src = url;

    // 设置 defer/async
    if (config.defer) script.defer = true;
    if (config.async) script.async = true;

    // 加载成功
    script.onload = () => {
      this.scripts[key].status = 'loaded';

      // 执行所有回调
      const callbacks = this.scripts[key].callbacks;
      this.scripts[key].callbacks = [];

      callbacks.forEach(cb => {
        try {
          if (config.useIdleCallback && window.requestIdleCallback) {
            requestIdleCallback(() => cb());
          } else {
            cb();
          }
        } catch (error) {
          console.error(`Error in callback for script "${key}":`, error);
        }
      });

      if (config.onProgress) {
        config.onProgress({ key, status: 'loaded' });
      }
    };

    // 加载失败
    script.onerror = (error) => {
      this.scripts[key].status = 'error';
      console.error(`Failed to load script "${key}" from ${url}`, error);

      if (config.onError) {
        config.onError(error);
      }
    };

    // 添加到 DOM
    document.head.appendChild(script);
  }

  /**
   * 加载多个脚本（按顺序）
   * @param {Array} scripts - 脚本数组 [{ key, url, options }]
   * @param {Function} callback - 所有脚本加载完成后的回调
   */
  loadMultiple(scripts, callback) {
    if (!scripts || scripts.length === 0) {
      if (callback) callback();
      return;
    }

    let loadedCount = 0;
    const totalCount = scripts.length;

    const checkComplete = () => {
      loadedCount++;
      if (loadedCount === totalCount && callback) {
        callback();
      }
    };

    scripts.forEach(script => {
      // 加载脚本
      this.load(script.key, script.url, script.options || {}, checkComplete);
    });
  }

  /**
   * 使用预定义配置加载库（推荐）
   * @param {string} libraryName - 库名称（embla/jquery/swiper等）
   * @param {Function} callback - 加载完成回调
   * @param {Object} customOptions - 可选的自定义配置（覆盖默认）
   */
  loadLibrary(libraryName, callback, customOptions = {}) {
    const config = LIBRARY_CONFIG[libraryName];

    if (!config) {
      console.error(
        `Library "${libraryName}" not found. ` +
        `Available libraries: ${Object.keys(LIBRARY_CONFIG).join(', ')}`
      );
      return;
    }

    // 从配置中读取 URL
    const url = config.url;
    const options = { ...config, ...customOptions };
    delete options.url; // 删除 url，避免传递给 load 的 options

    this.load(libraryName, url, options, callback);
  }

  /**
   * 批量加载多个库（使用预定义配置）
   * @param {Array} libraries - ['embla', 'jquery'] 或 [{ name: 'embla', options: {...} }]
   * @param {Function} callback - 所有库加载完成后的回调
   */
  loadLibraries(libraries, callback) {
    // 支持两种格式：
    // 1. ['embla', 'jquery'] - 纯库名数组
    // 2. [{ name: 'embla', options: {...} }] - 带自定义配置

    const scripts = libraries.map(lib => {
      const libraryName = typeof lib === 'string' ? lib : lib.name;
      const customOptions = typeof lib === 'object' && lib.options ? lib.options : {};
      const config = LIBRARY_CONFIG[libraryName];

      if (!config) {
        console.error(
          `Library "${libraryName}" not found. ` +
          `Available libraries: ${Object.keys(LIBRARY_CONFIG).join(', ')}`
        );
        return null;
      }

      return {
        key: libraryName,
        url: config.url,
        options: { ...config, ...customOptions }
      };
    }).filter(Boolean); // 过滤掉 null

    // 删除所有 options 中的 url 字段
    scripts.forEach(script => {
      if (script.options.url) {
        delete script.options.url;
      }
    });

    this.loadMultiple(scripts, callback);
  }

  /**
   * 获取加载状态
   * @param {string} key - 脚本键名
   * @returns {string|null} - 'loading'|'loaded'|'error'|null
   */
  getStatus(key) {
    return this.scripts[key] ? this.scripts[key].status : null;
  }
}

// 创建全局单例实例
if (!window.ScriptLoader) {
  window.ScriptLoader = new ScriptLoaderClass();
}
